'use client';

// eslint-disable-next-line import/no-unresolved
import { c as _c } from "react/compiler-runtime";
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef } from 'react';
import queryString from 'query-string';

/**
 * Manages the `pages` URL param for category pages.
 *
 * - Reads `pages` from the URL on mount so that navigating back restores
 *   the correct number of loaded pages (scroll restoration).
 * - `updateUrlParamImmediate` accepts `{ pages }` and writes it to the URL
 *   via `router.replace` (no scroll jump), matching the advanced-search
 *   behaviour in `useAdvancedSearchNextURL`.
 * - When the slug changes (different category) the `pages` param is cleared
 *   from the URL so the new category always starts at page 1.
 */
const useCategoryPageURL = slug => {
  const $ = _c(17);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams?.get("pages") ?? "0";
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const parsedPages = parseInt(t0, 10);
  let t1;
  if ($[2] !== parsedPages) {
    t1 = Number.isFinite(parsedPages) && parsedPages >= 0 ? parsedPages : 0;
    $[2] = parsedPages;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const pagesUrlParam = t1;
  const prevSlugRef = useRef(slug);
  let t2;
  let t3;
  if ($[4] !== pathname || $[5] !== router || $[6] !== searchParams || $[7] !== slug) {
    t2 = () => {
      if (prevSlugRef.current !== slug) {
        prevSlugRef.current = slug;
        const prev = Object.fromEntries(searchParams);
        if (prev.pages !== undefined) {
          const {
            pages: _removed,
            ...rest
          } = prev;
          const newPath = `${pathname}${!pathname.endsWith("/") ? "/" : ""}?${queryString.stringify(rest)}`;
          router.replace(newPath, {
            scroll: false
          });
        }
      }
    };
    t3 = [slug, pathname, router, searchParams];
    $[4] = pathname;
    $[5] = router;
    $[6] = searchParams;
    $[7] = slug;
    $[8] = t2;
    $[9] = t3;
  } else {
    t2 = $[8];
    t3 = $[9];
  }
  useEffect(t2, t3);
  let t4;
  if ($[10] !== pathname || $[11] !== router || $[12] !== searchParams) {
    t4 = updates => {
      if (!updates) {
        return;
      }
      const prev_0 = Object.fromEntries(searchParams);
      const merged = {
        ...prev_0,
        ...updates
      };
      Object.keys(merged).forEach(k => {
        if (merged[k] === undefined || merged[k] === null) {
          delete merged[k];
        }
      });
      const newPath_0 = `${pathname}${!pathname.endsWith("/") ? "/" : ""}?${queryString.stringify(merged)}`;
      router.replace(newPath_0, {
        scroll: false
      });
    };
    $[10] = pathname;
    $[11] = router;
    $[12] = searchParams;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const updateUrlParamImmediate = t4;
  let t5;
  if ($[14] !== pagesUrlParam || $[15] !== updateUrlParamImmediate) {
    t5 = {
      pagesUrlParam,
      updateUrlParamImmediate
    };
    $[14] = pagesUrlParam;
    $[15] = updateUrlParamImmediate;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
};
export default useCategoryPageURL;